import {
  Component,
  ElementRef,
  inject,
  Input,
  signal,
  ChangeDetectionStrategy,
} from '@angular/core';
import { Store } from '@ngrx/store';
import {
  ActivatedRoute,
  Router,
  RouterLink,
  RouterLinkActive,
} from '@angular/router';
import { IProject } from '@core/state/entities/project/project.entity';
import { IStage } from '@core/state/entities/stage/stage.entity';
import { firstValueFrom, Observable } from 'rxjs';
import {
  selectCurrentProject,
  selectUnderProjectRoute,
} from '@core/state/entities/project/project.selectors';
import {
  selectCurrentStage,
  selectUnderStageRoute,
} from '@core/state/entities/stage/stage.selectors';
import { selectLoadedProjectId } from '@core/state/ui/project/project-ui.selectors';
import { selectCurrentStageId } from '@core/state/ui/stage/stage-ui.selectors';
import { IUser } from '@core/auth/user.entity';
import {
  selectAuthenticatedUser,
  selectAuthIsIdle,
  selectCurrentUser,
  selectIsSyrfAdminUserAuthenticated,
  selectIsAuthenticated,
  selectIsAuthenticating,
  selectIsImpersonating,
  selectProfilePictureUrl,
} from '@core/auth/auth.selectors';
import { AppConfigService } from '@core/services/config/app-config.service';
import { selectStagesForCurrentProjectWithFavouritesFirst } from '@core/state/entities/stage-usage/stage-usage.selectors';
import {
  selectFavouriteProjects,
  selectFirstFiveRecentProjects,
} from '@core/state/entities/project-usage/project-usage.selectors';
import { FeatureToggleService } from '@core/services/feature-toggle/feature-toggle.service';
import { animate, style, transition, trigger } from '@angular/animations';
import { authApiActions } from '@core/auth/auth.actions';
import { v4 as uuid } from 'uuid';
import { GuidDecodeComponent } from '@app/admin/guid-decode/guid-decode.component';
import { MatDialog } from '@angular/material/dialog';
import { VersionInfoDialogComponent } from '@shared/dialogs/version-info-dialog/version-info-dialog.component';
import { EnvironmentWarningDialogComponent } from '../components/environment-warning-dialog/environment-warning-dialog.component';
import { EnvironmentChipComponent } from '../components/environment-chip/environment-chip.component';
import { AsyncPipe } from '@angular/common';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatMenuModule } from '@angular/material/menu';
import { HoverDirective } from '@shared/hover/hover.directive';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { FlexLayoutModule } from '@ngbracket/ngx-layout';
import { MatToolbarModule } from '@angular/material/toolbar';
import { projectDetailActions } from '@core/actions/project-detail.actions';
import { selectShowAdminUiOptions } from '@core/state/ui/admin/admin-ui.selectors';
import { adminActions } from '@core/actions/admin.actions';
import { ProjectHttpService } from '@core/services/api-client.generated';
import { ErrorRecoveryDialogComponent } from '../components/error-recovery-dialog/error-recovery-dialog.component';
import { UserGuideUrlPipe } from '@shared/pipes/user-guide-url/user-guide-url.pipe';
import { ProjectNavigationService } from '@core/services/layout/project-navigation.service';
import { LayoutService } from '@core/services/layout/layout.service';
export type NavType = 'project' | 'stage';

@Component({
  selector: 'app-nav',
  templateUrl: './nav.component.html',
  styleUrls: ['./nav.component.scss'],
  // eslint-disable-next-line @angular-eslint/no-host-metadata-property,@typescript-eslint/naming-convention
  host: { '[class.change-detecting]': 'changeDetection' },
  animations: [
    trigger('collapseInOut', [
      transition(':enter', [
        style({
          transform: 'translateX(20vw)',
          opacity: 0,
        }),
        animate(
          '0.5s',
          style({
            opacity: 1,
            transform: 'translateX(0)',
          })
        ),
      ]),
      transition(':leave', [
        style({
          transform: 'translateX(0)',
          opacity: 1,
        }),
        animate(
          '0.5s',
          style({
            opacity: 0,
            transform: 'translateX(20vw)',
          })
        ),
      ]),
    ]),
    trigger('appearDisappear', [
      transition(':enter', [
        style({
          opacity: 0,
        }),
        animate(
          '0.5s',
          style({
            opacity: 1,
          })
        ),
      ]),
      transition(':leave', [
        style({
          opacity: 1,
        }),
        animate(
          '0.5s',
          style({
            opacity: 0,
          })
        ),
      ]),
    ]),
  ],
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [
    MatToolbarModule,
    FlexLayoutModule,
    MatButtonModule,
    RouterLink,
    RouterLinkActive,
    MatIconModule,
    HoverDirective,
    MatMenuModule,
    MatTooltipModule,
    MatProgressSpinnerModule,
    AsyncPipe,
    EnvironmentChipComponent,
    UserGuideUrlPipe,
  ],
})
export class NavComponent {
  private _store: Store = inject(Store);
  public route = inject(ActivatedRoute);
  _router = inject(Router);
  _appConfigService = inject(AppConfigService);
  _featureToggleService = inject(FeatureToggleService);
  _el = inject(ElementRef);
  _dialog = inject(MatDialog);
  @Input()
  navType: NavType = 'project';
  @Input()
  appIsUnstable = false;
  projectId$: Observable<string | null> = this._store.select(
    selectLoadedProjectId
  );
  stageId$: Observable<string | null> =
    this._store.select(selectCurrentStageId);
  $underProjectRoute = this._store.selectSignal(selectUnderProjectRoute);
  $underStageRoute = this._store.selectSignal(selectUnderStageRoute);
  currentUser$: Observable<IUser | null> =
    this._store.select(selectCurrentUser);
  authenticated$: Observable<boolean> = this._store.select(
    selectIsAuthenticated
  );
  authenticating$: Observable<boolean> = this._store.select(
    selectIsAuthenticating
  );
  authIsIdle$: Observable<boolean> = this._store.select(selectAuthIsIdle);
  isImpersonatingUser$: Observable<boolean> = this._store.select(
    selectIsImpersonating
  );
  authenticatedUser$: Observable<IUser | null> = this._store.select(
    selectAuthenticatedUser
  );
  isAdmin$: Observable<boolean> = this._store.select(
    selectIsSyrfAdminUserAuthenticated
  );
  profilePictureUrl$: Observable<string | null> = this._store.select(
    selectProfilePictureUrl
  );
  currentProjectStages$: Observable<IStage[] | null> = this._store.select(
    selectStagesForCurrentProjectWithFavouritesFirst
  );
  favouriteProjects$: Observable<IProject[] | null> = this._store.select(
    selectFavouriteProjects
  );
  recentProjects$: Observable<IProject[] | null> = this._store.select(
    selectFirstFiveRecentProjects
  );
  showDebuggers$: Observable<boolean> =
    this._featureToggleService.showDebuggers$;
  showLogging$: Observable<boolean> = this._featureToggleService.showLogging$;
  showChangeDetection$: Observable<boolean> =
    this._featureToggleService.showChangeDetection$;
  changeDetection = false;
  randomId: string = this._appConfigService.config.randomId;
  showingAdminUiOptions = this._store.selectSignal(selectShowAdminUiOptions);
  currentProject = this._store.selectSignal(selectCurrentProject);
  currentStage = this._store.selectSignal(selectCurrentStage);
  _projectService = inject(ProjectHttpService);
  private readonly _layoutService = inject(LayoutService);
  readonly projectNavigation = inject(ProjectNavigationService);

  readonly compactNavigation = this._layoutService.compactApplicationShell;

  toggleProjectNavigation(): void {
    this.projectNavigation.toggle();
  }

  showErrorRecoveryDialog() {
    ErrorRecoveryDialogComponent.open(this._dialog);
  }
  async updateAnnotationAnswerTally() {
    const currentProject = this.currentProject();
    if (currentProject) {
      await firstValueFrom(
        this._projectService.updateAnnotationAnswerTally(currentProject.id)
      );
    }
  }
  blink() {
    // Under the zoneless bootstrap (wave 3) this setTimeout stops scheduling
    // change detection; until then zone.js still ticks on it. Harmless either
    // way because the blink components are OnPush.
    this._el.nativeElement.classList.add('highlight');
    setTimeout(() => {
      this._el.nativeElement.classList.remove('highlight');
    }, 1500);
  }

  signOut() {
    this._store.dispatch(authApiActions.signOut({ onlyLocal: false }));
  }

  setChangeDetectionVisualisation(showChangeDetection) {
    this._featureToggleService.showChangeDetection(showChangeDetection);
  }

  setDebuggers(showDebuggers) {
    this._featureToggleService.showDebuggers(showDebuggers);
  }

  setLogging(showLogging) {
    this._featureToggleService.showLogging(showLogging);
  }

  signIn() {
    this._store.dispatch(authApiActions.startSignIn({ redirectUrl: null }));
  }

  onThrowError() {
    throw Error('Mock Error!');
  }

  openGuidDecoder() {
    this._dialog.open(GuidDecodeComponent, {
      width: '500px',
    });
  }

  openVersionInfo() {
    this._dialog.open(VersionInfoDialogComponent, {
      width: '450px',
      autoFocus: false,
    });
  }

  openEnvironmentWarning() {
    this._dialog.open(EnvironmentWarningDialogComponent, {
      width: '450px',
      autoFocus: true,
      ariaDescribedBy: 'environment-warning-dialog-message',
    });
  }

  updateAllProjectInclusion() {
    this._store.dispatch(
      projectDetailActions.updateAllProjectStudyInclusionInfo({
        replaceExisting: true,
        correlationId: uuid(),
      })
    );
  }

  toggleAdminUiOptions() {
    this._store.dispatch(adminActions.toggleAdminUiOptions());
  }
}
